// Week/day navigation primitives shared by employee views and the builder.
import { addDays, todayLocal, weekDates, weekKeyFor } from '../lib/time'
import { formatWeekRange } from '../lib/format'
import { DateTime } from 'luxon'
import { TZ } from '../lib/time'

export function WeekPicker({
  weekStart,
  onChange,
}: {
  weekStart: string
  onChange: (weekStart: string) => void
}) {
  return (
    <div className="flex items-center gap-1">
      <button className="btn-ghost !min-h-[40px] px-2" onClick={() => onChange(addDays(weekStart, -7))} aria-label="Previous week">
        ‹
      </button>
      <button
        className="btn-ghost !min-h-[40px] px-2 font-bold text-interlochen"
        onClick={() => onChange(weekKeyFor(todayLocal()))}
        title="Jump to this week"
      >
        {formatWeekRange(weekStart)}
      </button>
      <button className="btn-ghost !min-h-[40px] px-2" onClick={() => onChange(addDays(weekStart, 7))} aria-label="Next week">
        ›
      </button>
    </div>
  )
}

export function DayStrip({
  weekStart,
  selected,
  onSelect,
  markedDates = new Set<string>(),
}: {
  weekStart: string
  selected: string
  onSelect: (date: string) => void
  markedDates?: Set<string>
}) {
  const today = todayLocal()
  return (
    <div className="sticky top-0 z-10 flex gap-1 overflow-x-auto bg-page py-2" role="tablist" aria-label="Days of the week">
      {weekDates(weekStart).map((date) => {
        const dt = DateTime.fromISO(date, { zone: TZ })
        const isSelected = date === selected
        return (
          <button
            key={date}
            role="tab"
            aria-selected={isSelected}
            onClick={() => onSelect(date)}
            className={`flex min-h-[52px] min-w-[46px] flex-1 flex-col items-center justify-center rounded-lg border ${
              isSelected
                ? 'border-interlochen bg-interlochen text-white'
                : date === today
                  ? 'border-interlochen bg-white text-interlochen'
                  : 'border-hairline bg-white text-ink'
            }`}
          >
            <span className="text-[11px] uppercase">{dt.toFormat('ccc')}</span>
            <span className="tnum text-sm font-bold">{dt.day}</span>
            {markedDates.has(date) && (
              <span className={`h-1.5 w-1.5 rounded-full ${isSelected ? 'bg-sunbeam' : 'bg-sunset'}`} aria-label="You work this day" />
            )}
          </button>
        )
      })}
    </div>
  )
}

export function HoursMeter({ hours, threshold }: { hours: number; threshold: number }) {
  const pct = Math.min(100, (hours / Math.max(threshold, 1)) * 100)
  const over = hours > threshold
  const near = !over && hours > threshold - 4
  return (
    <div>
      <div className="flex items-baseline justify-between">
        <span className="section-label">This week</span>
        <span className={`tnum font-bold ${over ? 'text-danger' : near ? 'text-clay' : 'text-ink'}`}>
          {hours} / {threshold}h {over && <span className="chip bg-danger/10 text-danger">OT</span>}
        </span>
      </div>
      <div className="mt-1 h-2 overflow-hidden rounded-full bg-hairline" role="presentation">
        <div
          className={`h-full rounded-full ${over ? 'bg-danger' : near ? 'bg-sunset' : 'bg-interlochen'}`}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  )
}
